import { useRef, useState, useEffect, ReactNode } from 'react';

interface LazySectionProps {
  children: ReactNode;
  className?: string;
  /** Root margin for intersection observer - how early to start loading */
  rootMargin?: string;
  /** Minimum height placeholder to prevent layout shift */
  minHeight?: string;
  /** Fallback content while loading */
  fallback?: ReactNode;
}

/**
 * LazySection - Only renders children when the section enters viewport
 * Improves initial page load by deferring below-the-fold content
 */
export function LazySection({
  children,
  className = '',
  rootMargin = '200px',
  minHeight = '400px',
  fallback
}: LazySectionProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    // Check if already in viewport on mount
    const rect = element.getBoundingClientRect();
    if (rect.top < window.innerHeight + 200) {
      setIsVisible(true);
      setHasLoaded(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          setHasLoaded(true);
          observer.disconnect();
        }
      },
      {
        rootMargin,
        threshold: 0
      }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [rootMargin]);

  return (
    <div
      ref={ref}
      className={className}
      style={{ minHeight: hasLoaded ? 'auto' : minHeight }}
    >
      {isVisible ? children : (fallback || null)}
    </div>
  );
}

export default LazySection;
